import { createTheme } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';

const CHIP_SUCCESS_LIGHT = '#4caf50';
const CHIP_SUCCESS_DARK  = '#66BB6A';
const CHIP_WARNING_LIGHT = '#ed6c02';
const CHIP_WARNING_DARK  = '#FFA726';

const classicTheme = {
  muiTheme: createTheme({
    palette: {
      primary: { main: '#BF00FF' },
      success: { main: '#4caf50' },
      background: { default: '#eeeeee' },
    },
  }),
  cssVars: {
    '--app-bg': '#eeeeee',
    '--correct-border': 'green',
    '--correct-bg': 'rgba(0, 255, 0, 0.1)',
    '--correct-text': 'green',
  },
  colorLabels: { correct: 'Green', partial: 'Amber', miss: 'Grey' },
  globalStyles: null,
};

const retro70sGlobalStyles = (
  <GlobalStyles styles={`
    @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
    body { font-family: 'Bebas Neue', Impact, sans-serif !important; font-size: 18px !important; }
  `} />
);

const makeCrtStyles = (phosphorColor, phosphorGlow, scanlineColor) => (
  <GlobalStyles styles={`
    @import url('https://fonts.googleapis.com/css2?family=VT323&display=swap');

    @keyframes retroScanline {
      0%   { background-position: 0 0; }
      100% { background-position: 0 4px; }
    }

    @keyframes retroFlicker {
      0%, 89%, 91%, 95%, 97%, 100% { opacity: 1; }
      90%, 96% { opacity: 0.92; }
    }

    body {
      font-family: 'VT323', 'Courier New', monospace !important;
      font-size: 18px !important;
      color: ${phosphorColor} !important;
      text-shadow: 0 0 8px ${phosphorGlow};
      animation: retroFlicker 6s ease-in-out infinite;
    }

    ::selection {
      background: ${phosphorColor};
      color: #000;
    }

    body::before {
      content: '';
      position: fixed;
      inset: 0;
      pointer-events: none;
      background: radial-gradient(ellipse at center, transparent 55%, rgba(0, 0, 0, 0.75) 100%);
      z-index: 9998;
    }

    body::after {
      content: '';
      position: fixed;
      inset: 0;
      pointer-events: none;
      background: repeating-linear-gradient(
        to bottom,
        transparent 0px,
        transparent 2px,
        ${scanlineColor} 2px,
        ${scanlineColor} 4px
      );
      animation: retroScanline 0.12s linear infinite;
      z-index: 9999;
    }
  `} />
);

const retroTheme70s = {
  muiTheme: createTheme({
    palette: {
      mode: 'dark',
      primary: { main: '#F5E6C8' },
      secondary: { main: '#FFB300' },
      success: { main: '#9CCC65' },
      warning: { main: '#FFB300' },
      background: { default: '#1B3A1B', paper: '#142D14' },
      text: { primary: '#F5E6C8', secondary: '#C8B896' },
    },
    typography: {
      fontFamily: '"Bebas Neue", Impact, sans-serif',
      fontSize: 16,
    },
  }),
  cssVars: {
    '--app-bg': '#1B3A1B',
    '--correct-border': '#F5E6C8',
    '--correct-bg': 'rgba(245, 230, 200, 0.12)',
    '--correct-text': '#F5E6C8',
    '--guess-border': 'rgba(245, 230, 200, 0.35)',
  },
  colorLabels: { correct: 'Green', partial: 'Amber', miss: 'Dark' },
  globalStyles: retro70sGlobalStyles,
};

const makeSynthwaveStyles = (phosphorColor, phosphorGlow, scanlineColor) => (
  <GlobalStyles styles={`
    @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700&display=swap');

    @keyframes retroScanline80s {
      0%   { background-position: 0 0; }
      100% { background-position: 0 4px; }
    }

    @keyframes neonGlow80s {
      0%, 100% { text-shadow: 0 0 6px ${phosphorGlow}; }
      50%       { text-shadow: 0 0 12px ${phosphorGlow}, 0 0 24px ${phosphorGlow}; }
    }

    body {
      font-family: 'Orbitron', sans-serif !important;
      font-size: 12px !important;
      color: #E0E0FF !important;
      text-shadow: 0 0 4px ${phosphorGlow};
    }

    ::selection { background: ${phosphorColor}; color: #000; }

    body::before {
      content: '';
      position: fixed;
      inset: 0;
      pointer-events: none;
      background: radial-gradient(ellipse at center, transparent 55%, rgba(0, 0, 0, 0.75) 100%);
      z-index: 9998;
    }

    body::after {
      content: '';
      position: fixed;
      inset: 0;
      pointer-events: none;
      background: repeating-linear-gradient(
        to bottom,
        transparent 0px,
        transparent 2px,
        ${scanlineColor} 2px,
        ${scanlineColor} 4px
      );
      animation: retroScanline80s 0.12s linear infinite;
      z-index: 9999;
    }
  `} />
);

const retroTheme80s = {
  muiTheme: createTheme({
    palette: {
      mode: 'dark',
      primary: { main: '#FF1493' },
      secondary: { main: '#00BFFF' },
      success: { main: '#00E676' },
      warning: { main: '#FFD740' },
      background: { default: '#070010', paper: '#0E0020' },
      text: { primary: '#E0E0FF', secondary: '#A0A0CC' },
    },
    typography: {
      fontFamily: '"Orbitron", sans-serif',
      fontSize: 12,
    },
  }),
  cssVars: {
    '--app-bg': '#070010',
    '--correct-border': '#FF1493',
    '--correct-bg': 'rgba(255, 20, 147, 0.12)',
    '--correct-text': '#FF1493',
    '--guess-border': 'rgba(255, 20, 147, 0.40)',
  },
  colorLabels: { correct: 'Green', partial: 'Amber', miss: 'Dark' },
  globalStyles: makeSynthwaveStyles('#FF1493', 'rgba(255, 20, 147, 0.6)', 'rgba(0, 0, 0, 0.14)'),
};


const win98GlobalStyles = (
  <GlobalStyles styles={`
    body {
      font-family: Tahoma, 'MS Sans Serif', Geneva, sans-serif !important;
      font-size: 12px !important;
      color: #000000 !important;
    }

    .MuiPaper-outlined {
      border-radius: 0 !important;
      background: #C0C0C0 !important;
      box-shadow:
        inset -1px -1px #0a0a0a,
        inset 1px 1px #ffffff,
        inset -2px -2px #808080,
        inset 2px 2px #dfdfdf !important;
    }

    .MuiChip-root {
      border-radius: 0 !important;
      box-shadow:
        inset -1px -1px #0a0a0a,
        inset 1px 1px #ffffff,
        inset -2px -2px #808080,
        inset 2px 2px #dfdfdf !important;
    }
  `} />
);

const retro00sGlobalStyles = (
  <GlobalStyles styles={`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500&display=swap');

    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
      font-size: 14px !important;
      background-color: #D8CAEE !important;
      background-image:
        radial-gradient(ellipse at 5% 35%, rgba(255, 135, 100, 0.72) 0%, transparent 45%),
        radial-gradient(ellipse at 88% 5%, rgba(195, 155, 235, 0.68) 0%, transparent 45%),
        radial-gradient(ellipse at 95% 88%, rgba(70, 210, 195, 0.68) 0%, transparent 45%),
        radial-gradient(ellipse at 12% 92%, rgba(145, 190, 240, 0.60) 0%, transparent 40%) !important;
      background-attachment: fixed !important;
    }

    .MuiPaper-outlined {
      position: relative !important;
      border-radius: 16px !important;
      overflow: hidden !important;
      background: rgba(255, 255, 255, 0.18) !important;
      backdrop-filter: blur(36px) saturate(220%) brightness(1.06) !important;
      -webkit-backdrop-filter: blur(36px) saturate(220%) brightness(1.06) !important;
      border: 1px solid rgba(255, 255, 255, 0.55) !important;
      box-shadow:
        inset 0 2px 0 rgba(255, 255, 255, 0.90),
        inset 0 -1px 0 rgba(255, 255, 255, 0.15),
        inset 1px 0 0 rgba(255, 255, 255, 0.45),
        inset -1px 0 0 rgba(255, 255, 255, 0.20),
        0 16px 40px rgba(0, 0, 0, 0.10),
        0 4px 12px rgba(0, 0, 0, 0.06) !important;
    }

    .MuiPaper-outlined::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 45%;
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0.55) 0%,
        rgba(255, 255, 255, 0.00) 100%
      );
      pointer-events: none;
    }

    .MuiChip-root {
      border-radius: 10px !important;
    }

    .MuiOutlinedInput-root {
      border-radius: 12px !important;
    }
  `} />
);

const retroTheme90s = {
  muiTheme: createTheme({
    palette: {
      mode: 'light',
      primary: { main: '#000080' },
      secondary: { main: '#808080' },
      success: { main: CHIP_SUCCESS_LIGHT },
      warning: { main: CHIP_WARNING_LIGHT },
      background: { default: '#9DC4C4', paper: '#C0C0C0' },
      text: { primary: '#000000', secondary: '#444444' },
    },
    typography: {
      fontFamily: "Tahoma, 'MS Sans Serif', Geneva, sans-serif",
      fontSize: 12,
    },
  }),
  cssVars: {
    '--app-bg': '#9DC4C4',
    '--correct-border': 'green',
    '--correct-bg': 'rgba(0, 255, 0, 0.1)',
    '--correct-text': 'green',
    '--guess-border': 'transparent',
  },
  colorLabels: { correct: 'Green', partial: 'Amber', miss: 'Grey' },
  globalStyles: win98GlobalStyles,
};

const retroTheme00s = {
  muiTheme: createTheme({
    palette: {
      primary: { main: '#0071e3' },
      secondary: { main: '#8E8E93' },
      success: { main: CHIP_SUCCESS_LIGHT },
      warning: { main: CHIP_WARNING_LIGHT },
      background: { default: '#D8CAEE' },
    },
    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: 14,
    },
  }),
  cssVars: {
    '--app-bg': '#D8CAEE',
    '--correct-border': 'green',
    '--correct-bg': 'rgba(0, 255, 0, 0.1)',
    '--correct-text': 'green',
    '--guess-border': 'rgba(255, 255, 255, 0.80)',
  },
  colorLabels: { correct: 'Green', partial: 'Amber', miss: 'Light' },
  globalStyles: retro00sGlobalStyles,
};

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const makeTeamTheme = ({ primary, secondary, bg, bgPaper, mode = 'light', missLabel }) => {
  const chipSuccess = mode === 'dark' ? CHIP_SUCCESS_DARK : CHIP_SUCCESS_LIGHT;
  const chipWarning = mode === 'dark' ? CHIP_WARNING_DARK : CHIP_WARNING_LIGHT;
  return {
    muiTheme: createTheme({
      palette: {
        mode,
        primary: { main: primary },
        secondary: { main: secondary },
        success: { main: chipSuccess },
        warning: { main: chipWarning },
        background: { default: bg, ...(bgPaper ? { paper: bgPaper } : {}) },
      },
    }),
    cssVars: {
      '--app-bg': bg,
      '--correct-border': primary,
      '--correct-bg': hexToRgba(primary, 0.12),
      '--correct-text': primary,
      '--guess-border': hexToRgba(primary, 0.35),
    },
    colorLabels: { correct: 'Green', partial: 'Amber', miss: missLabel },
    globalStyles: null,
  };
};

const teamThemes = {
  'team-adelaide-crows': makeTeamTheme({
    primary: '#002B5C', secondary: '#CC0000', bg: '#f0f4f8',
    missLabel: 'Grey',
  }),
  'team-brisbane-lions': makeTeamTheme({
    mode: 'dark', primary: '#FFC72C', secondary: '#8B1118', bg: '#0a0a14', bgPaper: '#12121e',
    missLabel: 'Dark',
  }),
  'team-carlton-blues': makeTeamTheme({
    primary: '#001F5B', secondary: '#6699CC', bg: '#f0f4f8',
    missLabel: 'Grey',
  }),
  'team-collingwood-magpies': makeTeamTheme({
    mode: 'dark', primary: '#FFFFFF', secondary: '#aaaaaa', bg: '#0d0d0d', bgPaper: '#1a1a1a',
    missLabel: 'Dark',
  }),
  'team-essendon-bombers': makeTeamTheme({
    mode: 'dark', primary: '#CC0000', secondary: '#ff4444', bg: '#0d0000', bgPaper: '#1a0000',
    missLabel: 'Dark',
  }),
  'team-fremantle-dockers': makeTeamTheme({
    primary: '#2E1461', secondary: '#00B140', bg: '#f5f0ff',
    missLabel: 'Grey',
  }),
  'team-geelong-cats': makeTeamTheme({
    primary: '#002C73', secondary: '#6B8CBF', bg: '#f0f4f8',
    missLabel: 'Grey',
  }),
  'team-gold-coast-suns': makeTeamTheme({
    mode: 'dark', primary: '#FFB81C', secondary: '#E4002B', bg: '#140800', bgPaper: '#1f1000',
    missLabel: 'Dark',
  }),
  'team-greater-western-sydney-giants': makeTeamTheme({
    primary: '#F47920', secondary: '#5C5C5C', bg: '#f5f0ec',
    missLabel: 'Light',
  }),
  'team-hawthorn-hawks': makeTeamTheme({
    mode: 'dark', primary: '#FFD200', secondary: '#aa8800', bg: '#1a0d01', bgPaper: '#261200',
    missLabel: 'Dark',
  }),
  'team-melbourne-demons': makeTeamTheme({
    primary: '#CC0000', secondary: '#003090', bg: '#f5f5f5',
    missLabel: 'Grey',
  }),
  'team-kangaroos': makeTeamTheme({
    primary: '#003E7E', secondary: '#E41936', bg: '#f0f4f8',
    missLabel: 'Grey',
  }),
  'team-port-adelaide-power': makeTeamTheme({
    mode: 'dark', primary: '#008AAB', secondary: '#8C8F8E', bg: '#060a0b', bgPaper: '#0b1215',
    missLabel: 'Dark',
  }),
  'team-richmond-tigers': makeTeamTheme({
    mode: 'dark', primary: '#FFD100', secondary: '#cc9900', bg: '#0d0d00', bgPaper: '#1a1a00',
    missLabel: 'Dark',
  }),
  'team-st-kilda-saints': makeTeamTheme({
    mode: 'dark', primary: '#E51837', secondary: '#cccccc', bg: '#0d0000', bgPaper: '#1a0005',
    missLabel: 'Dark',
  }),
  'team-sydney-swans': makeTeamTheme({
    primary: '#E4002B', secondary: '#ff6666', bg: '#fff5f5',
    missLabel: 'Grey',
  }),
  'team-west-coast-eagles': makeTeamTheme({
    primary: '#003087', secondary: '#F2A900', bg: '#f0f4f8',
    missLabel: 'Grey',
  }),
  'team-western-bulldogs': makeTeamTheme({
    primary: '#003087', secondary: '#CC0000', bg: '#f0f4f8',
    missLabel: 'Grey',
  }),
};

const defaultTheme = {
  muiTheme: createTheme({
    palette: {
      primary: { main: '#455a64' },
      success: { main: '#4caf50' },
      background: { default: '#f5f5f5' },
    },
  }),
  cssVars: {
    '--app-bg': '#f5f5f5',
    '--correct-border': '#4caf50',
    '--correct-bg': 'rgba(76, 175, 80, 0.1)',
    '--correct-text': '#4caf50',
  },
  colorLabels: { correct: 'Green', partial: 'Amber', miss: 'Grey' },
  globalStyles: null,
};

const colourBlindTheme = {
  muiTheme: createTheme({
    palette: {
      primary: { main: '#0077BB' },
      success: { main: '#0077BB' },
      warning: { main: '#EE7733' },
      background: { default: '#f5f5f5' },
    },
  }),
  cssVars: {
    '--app-bg': '#f5f5f5',
    '--correct-border': '#0077BB',
    '--correct-bg': 'rgba(0, 119, 187, 0.12)',
    '--correct-text': '#0077BB',
  },
  colorLabels: { correct: 'Blue', partial: 'Orange', miss: 'Grey' },
  globalStyles: null,
};

export function getColourBlindTheme() { return colourBlindTheme; }

const THEME_REGISTRY = {
  'classic': classicTheme,
  'retro-70s': retroTheme70s,
  'retro-80s': retroTheme80s,
  'retro-90s': retroTheme90s,
  'retro-00s': retroTheme00s,
  ...teamThemes,
};

export function getThemeForSlug(slug) {
  if (!slug) return defaultTheme;
  return THEME_REGISTRY[slug.toLowerCase()] ?? defaultTheme;
}

